import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import { get, set, del } from 'idb-keyval';
import { queryClient } from './queryClient';

const IDB_KEY = 'mf-team-assistant-react-query';

/** Keep persisted chat cache for 24h (server + sync-meta still authoritative). */
export const TEAM_ASSISTANT_PERSIST_MAX_AGE = 24 * 60 * 60 * 1000;

const idbStorage = {
  getItem: (key) => get(key),
  setItem: (key, value) => set(key, value),
  removeItem: (key) => del(key),
};

function trimDehydratedState(clientState) {
  if (!clientState?.queries?.length) return clientState;

  const queries = clientState.queries.map((entry) => {
    const key = entry.queryKey;
    if (key?.[0] !== 'team-assistant' || key?.[1] !== 'messages' || key?.[2] !== 'chat') {
      return entry;
    }
    const data = entry.state?.data;
    if (!data?.pages || data.pages.length <= 1) return entry;

    return {
      ...entry,
      state: {
        ...entry.state,
        data: {
          ...data,
          pages: data.pages.slice(0, 1),
          pageParams: Array.isArray(data.pageParams) ? data.pageParams.slice(0, 1) : data.pageParams,
        },
      },
    };
  });

  return { ...clientState, queries };
}

const basePersister = createAsyncStoragePersister({
  storage: idbStorage,
  key: IDB_KEY,
  throttleTime: 1500,
});

export const teamAssistantQueryPersister = {
  ...basePersister,
  persistClient: async (persistedClient) => {
    const trimmed = {
      ...persistedClient,
      clientState: trimDehydratedState(persistedClient.clientState),
    };
    return basePersister.persistClient(trimmed);
  },
};

export function teamAssistantPersistBuster(userId) {
  return userId != null ? `user-${userId}` : 'logged-out';
}

export function shouldPersistTeamAssistantQuery(query) {
  if (query.state.status !== 'success') return false;
  const key = query.queryKey;
  if (!Array.isArray(key) || key[0] !== 'team-assistant') return false;
  if (key[1] === 'sync-meta') return true;
  if (key[1] === 'messages' && key[2] === 'chat') return true;
  return false;
}

export async function clearTeamAssistantQueryPersist(userId) {
  if (userId == null) return;
  try {
    await teamAssistantQueryPersister.removeClient();
    queryClient.removeQueries({ queryKey: ['team-assistant', 'messages'] });
    queryClient.removeQueries({ queryKey: ['team-assistant', 'sync-meta'] });
  } catch (e) {
    console.warn('Team Assistant query cache clear failed:', e);
  }
}
